import {
  EventType,
  PublicClientApplication,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';

/**
 * Shape of `public/appsettings.json`. Kept identical to the Blazor original so the
 * same deployment-time file swap keeps working: the OIDC provider options live
 * under the `Local` key.
 */
export interface OidcProviderOptions {
  Authority: string;
  ClientId: string;
}

interface AppSettings {
  Local?: OidcProviderOptions;
}

/** Scopes requested at login. `openid`/`profile` give us the display name. */
export const loginRequest = { scopes: ['openid', 'profile'] };

async function loadProviderOptions(): Promise<OidcProviderOptions | null> {
  try {
    const response = await fetch('/appsettings.json', { cache: 'no-cache' });
    if (!response.ok) return null;

    const settings = (await response.json()) as AppSettings;
    const options = settings.Local;
    if (!options?.Authority || !options?.ClientId) return null;

    return options;
  } catch {
    return null;
  }
}

function buildConfiguration(options: OidcProviderOptions): Configuration {
  return {
    auth: {
      clientId: options.ClientId,
      authority: options.Authority,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  };
}

/**
 * Builds and initializes MSAL from the runtime config file.
 *
 * Returns `null` when authentication is not configured (or the authority is
 * unreachable) so the rest of the site still renders — every page here is
 * publicly viewable, exactly as it was under Blazor's `AuthorizeRouteView`
 * with no `[Authorize]` attributes.
 */
export async function createMsalInstance(): Promise<PublicClientApplication | null> {
  const options = await loadProviderOptions();
  if (!options) {
    console.warn(
      '[auth] No OIDC provider options found in /appsettings.json — sign-in is disabled.',
    );
    return null;
  }

  try {
    const instance = new PublicClientApplication(buildConfiguration(options));
    await instance.initialize();

    const [firstAccount] = instance.getAllAccounts();
    if (firstAccount && !instance.getActiveAccount()) {
      instance.setActiveAccount(firstAccount);
    }

    instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const payload = event.payload as AuthenticationResult;
        if (payload?.account) instance.setActiveAccount(payload.account);
      }
    });

    return instance;
  } catch (error) {
    console.warn('[auth] MSAL failed to initialize — sign-in is disabled.', error);
    return null;
  }
}
